import { useEffect, useRef } from 'react';

export function PainterlyWater({ lightIntensity = 1, onMouseMove }) {
  const canvasRef = useRef(null);
  const animationFrameRef = useRef(null);
  const mouseRef = useRef({ x: 0.5, y: 0.5, targetX: 0.5, targetY: 0.5 });
  const timeRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Setup canvas size
    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.scale(dpr, dpr);
    };
    resize();
    window.addEventListener('resize', resize);

    // Moon reflection configuration
    const reflectionConfig = {
      centerX: 0.5, // horizontal center (0-1)
      startY: 0.28, // where reflection starts (0-1) - just below horizon line
      endY: 0.95,   // where reflection ends (0-1)
      numStreaks: 10, // number of horizontal light streaks
      baseColor: { r: 255, g: 226, b: 188 }, // warm gold (#ffe2bc)
      darkColor: { r: 34, g: 65, b: 93 }, // deep water blue (#22415d)
    };

    // Create reflection streaks with depth perspective
    const createStreaks = () => {
      const streaks = [];
      for (let i = 0; i < reflectionConfig.numStreaks; i++) {
        const progress = i / (reflectionConfig.numStreaks - 1);
        const y = reflectionConfig.startY + progress * (reflectionConfig.endY - reflectionConfig.startY);

        // REVERSED: wider at horizon (top), narrower toward foreground (bottom)
        const depthFactor = Math.pow(progress, 1.5);
        // Start very wide at horizon, get narrower as we go down
        const width = 600 - depthFactor * 450; // 600px at horizon, down to 150px at foreground

        streaks.push({
          y,
          baseWidth: width,
          width,
          offset: 0,
          targetOffset: 0,
          opacity: 0.5 + Math.random() * 0.2,
          baseOpacity: 0.5 + Math.random() * 0.2,
          phase: Math.random() * Math.PI * 2, // for ambient shimmer
          speed: 0.3 + depthFactor * 0.7, // slower at horizon, faster at foreground
          depthFactor,
          segments: 2 + Math.floor(depthFactor * 3), // more fragmented toward foreground
        });
      }
      return streaks;
    };

    let streaks = createStreaks();

    // Mouse interaction
    const handleMouseMove = (e) => {
      if (prefersReducedMotion) return;
      const rect = canvas.getBoundingClientRect();
      mouseRef.current.targetX = e.clientX / rect.width;
      mouseRef.current.targetY = (e.clientY - rect.top) / rect.height;

      // Notify parent of mouse position for sailboat
      if (onMouseMove) {
        onMouseMove(mouseRef.current.targetX, mouseRef.current.targetY);
      }
    };

    const handleMouseLeave = () => {
      mouseRef.current.targetX = 0.5;
      mouseRef.current.targetY = 0.5;
    };

    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseleave', handleMouseLeave);

    // Perlin-like noise function for organic variation
    const noise = (x, y) => {
      return Math.sin(x * 2.3 + y * 3.7) * Math.cos(x * 1.7 - y * 2.1) * 0.5 + 0.5;
    };

    // Render loop
    const render = () => {
      const rect = canvas.getBoundingClientRect();
      const w = rect.width;
      const h = rect.height;

      // Clear canvas
      ctx.clearRect(0, 0, w, h);

      // Update time
      if (!prefersReducedMotion) {
        timeRef.current += 0.01;
      }

      // Update mouse position with inertia (smooth following)
      const inertia = 0.05;
      mouseRef.current.x += (mouseRef.current.targetX - mouseRef.current.x) * inertia;
      mouseRef.current.y += (mouseRef.current.targetY - mouseRef.current.y) * inertia;

      // Calculate mouse influence on reflection
      const mouseX = mouseRef.current.x;
      const mouseY = mouseRef.current.y;

      // Get current light intensity value
      const currentLightIntensity = typeof lightIntensity === 'object' && lightIntensity.get ? lightIntensity.get() : lightIntensity;

      // Render each streak
      streaks.forEach((streak, i) => {
        const streakY = streak.y * h;

        // Distance from mouse to this streak
        const distanceY = Math.abs(mouseY - streak.y);
        const distanceX = Math.abs(mouseX - reflectionConfig.centerX);
        const distance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

        // Mouse influence (stronger when close to reflection)
        const mouseInfluence = Math.max(0, 1 - distance * 3);

        // Ambient movement
        const ambientShimmer = Math.sin(timeRef.current * 0.5 + streak.phase) * 15 * streak.depthFactor;
        const ambientOffset = Math.cos(timeRef.current * 0.3 + i * 0.2) * 5 * streak.depthFactor;

        // Mouse-induced distortion
        const mouseDistortion = mouseInfluence * (mouseX - reflectionConfig.centerX) * 80 * (1 + streak.depthFactor);

        // Update streak offset with inertia
        streak.targetOffset = ambientOffset + mouseDistortion;
        streak.offset += (streak.targetOffset - streak.offset) * 0.08;

        // Width variation from mouse and ambient
        const widthVariation = ambientShimmer + mouseInfluence * 20;
        // Narrow the reflection as light intensity decreases (going deeper)
        streak.width = (streak.baseWidth + widthVariation) * (0.3 + currentLightIntensity * 0.7);

        // Opacity flicker
        const opacityFlicker = Math.sin(timeRef.current * 2 + streak.phase) * 0.1;
        const mouseOpacityBoost = mouseInfluence * 0.2;
        const baseCurrentOpacity = Math.max(0.1, Math.min(0.9,
          streak.baseOpacity + opacityFlicker + mouseOpacityBoost
        ));
        // Apply light intensity based on ocean depth
        const currentOpacity = baseCurrentOpacity * currentLightIntensity;

        // Draw streak with segments (more fragmented toward foreground)
        const segmentCount = streak.segments;
        const segmentSpacing = 20 + streak.depthFactor * 35; // Larger spacing between segments

        for (let seg = 0; seg < segmentCount; seg++) {
          const segmentOffset = (seg - segmentCount / 2) * segmentSpacing;
          // Make segments wider - each segment takes up more of the total width
          const segmentWidth = streak.width / segmentCount * (1.2 + Math.random() * 0.4);
          const segmentX = w * reflectionConfig.centerX + streak.offset + segmentOffset;

          // Add organic variation
          const noiseVal = noise(i * 0.5, timeRef.current * 0.2 + seg);
          const organicOffset = (noiseVal - 0.5) * 15 * streak.depthFactor;

          // Create gradient for soft edges
          const gradient = ctx.createLinearGradient(
            segmentX - segmentWidth / 2,
            streakY,
            segmentX + segmentWidth / 2,
            streakY
          );

          const { r, g, b } = reflectionConfig.baseColor;
          gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, 0)`);
          gradient.addColorStop(0.3, `rgba(${r}, ${g}, ${b}, ${currentOpacity * 0.6})`);
          gradient.addColorStop(0.5, `rgba(${r}, ${g}, ${b}, ${currentOpacity})`);
          gradient.addColorStop(0.7, `rgba(${r}, ${g}, ${b}, ${currentOpacity * 0.6})`);
          gradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);

          // Draw rounded streak segment - horizontal lines (wider than tall)
          ctx.fillStyle = gradient;
          ctx.filter = 'blur(2px)';
          const height = 3 + streak.depthFactor * 6; // Thinner horizontal lines
          ctx.fillRect(
            segmentX - segmentWidth / 2 + organicOffset,
            streakY - height / 2,
            segmentWidth,
            height
          );
          ctx.filter = 'none';
        }
      });

      animationFrameRef.current = requestAnimationFrame(render);
    };

    render();

    // Cleanup
    return () => {
      window.removeEventListener('resize', resize);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseleave', handleMouseLeave);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="painterly-water-canvas"
      style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'auto',
        zIndex: 1,
      }}
    />
  );
}
